import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/shared/contexts/AuthContext'
import { getProfile, updateProfile } from '@/modules/profile'
import type { Profile as ProfileType } from '@/modules/profile/profileTypes'
import { MainLayout } from '@/shared/components/layout'
import { Button, Alert } from '@/shared/components/ui'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import { formatPhone, validatePhone } from '@/lib/utils/deviceDetection'

export default function Profile() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [user, setUser] = useState<any>(null)
  const { user: authUser, loading: authLoading } = useAuth()
  const [profile, setProfile] = useState<ProfileType | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const [fullName, setFullName] = useState('')
  const [phone, setPhone] = useState('')
  const [whatsapp, setWhatsapp] = useState('')
  const [facebook, setFacebook] = useState('')
  const [instagram, setInstagram] = useState('')
  const [tiktok, setTiktok] = useState('')

  useEffect(() => {
    if (authLoading) return
    if (!authUser) {
      navigate('/login')
      return
    }
    setUser(authUser)
    loadProfile(authUser.id)
  }, [authUser, authLoading, navigate])

  const loadProfile = async (userId: string) => {
    setLoading(true)
    setError(null)

    try {
      const userProfile = await getProfile(userId)
      if (userProfile) {
        setProfile(userProfile)
        setFullName(userProfile.full_name || '')
        setPhone(userProfile.phone || '')
        setWhatsapp(userProfile.whatsapp || userProfile.phone || '')
        setFacebook(userProfile.facebook || '')
        setInstagram(userProfile.instagram || '')
        setTiktok(userProfile.tiktok || '')
      }
    } catch (err: any) {
      console.error('Error loading profile:', err)
      setError(t('profile.loadError'))
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)

    if (!fullName.trim()) {
      setError(t('auth.errorNameRequired'))
      return
    }

    setSaving(true)

    try {
      await updateProfile(user.id, {
        full_name: fullName.trim(),
        phone: phone ? formatPhone(phone) : undefined,
        whatsapp: whatsapp ? formatPhone(whatsapp) : undefined,
        facebook: facebook.trim() || undefined,
        instagram: instagram.trim() || undefined,
        tiktok: tiktok.trim() || undefined,
      })

      setSuccess(t('profile.updateSuccess'))
      await loadProfile(user.id)
    } catch (err: any) {
      console.error('Error updating profile:', err)
      setError(t('profile.updateError'))
    } finally {
      setSaving(false)
    }
  }

  if (!user) {
    return <MainLayout userName={user?.email} userId={user?.id}><div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" /></div></MainLayout>
  }

  return (
    <MainLayout userName={user?.email} userId={user?.id}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">{t('profile.title')}</h1>
        
        {error && <Alert variant="error" className="mb-6">{error}</Alert>}
        {success && <Alert variant="success" className="mb-6">{success}</Alert>}

        {loading ? (
          <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" /></div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 bg-white dark:bg-slate-800 p-6 rounded-lg">
            <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder={t('auth.fullName')} className="w-full p-3 rounded-lg border" />
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t('auth.phone')} className="w-full p-3 rounded-lg border" />
            <input type="text" value={facebook} onChange={(e) => setFacebook(e.target.value)} placeholder={t('auth.facebook')} className="w-full p-3 rounded-lg border" />
            <Button type="submit" loading={saving}>{t('profile.save')}</Button>
          </form>
        )}
      </div>
    </MainLayout>
  )
}
